(function () {
  var SECTION_SELECTOR = '.oddit-section-benefits-carousel';
  var TRACK_SELECTOR = '[data-carousel-track]';

  /* ZAP-224: the track is overflow-x:auto with scroll-snap, so a wheel, a
     trackpad swipe and a touch drag all scrolled it — but a mouse press-and-
     drag did nothing, because nothing translated pointer movement into
     scrollLeft. Touch and trackpad are left alone (the native behaviour is
     better than anything we'd emulate); this only wires up the mouse. */

  var DRAG_THRESHOLD = 4;

  function initTrack(track) {
    if (!track || track.dataset.dragInit === 'true') return;
    track.dataset.dragInit = 'true';

    var pointerId = null;
    var startX = 0;
    var startScrollLeft = 0;
    var snapBack = '';
    var moved = false;

    function onPointerDown(e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;

      pointerId = e.pointerId;
      startX = e.clientX;
      startScrollLeft = track.scrollLeft;
      moved = false;

      /* scroll-snap-type: x mandatory yanks scrollLeft back to the nearest card
         on every assignment, so the track has to come out of snap for the
         duration of the drag and go back in on release. */
      snapBack = track.style.scrollSnapType;
      track.style.scrollSnapType = 'none';
      track.classList.add('is-dragging');
    }

    function onPointerMove(e) {
      if (pointerId === null || e.pointerId !== pointerId) return;

      var delta = e.clientX - startX;

      if (!moved) {
        if (Math.abs(delta) < DRAG_THRESHOLD) return;
        moved = true;
        /* Captured only once the gesture is definitely a drag, so a plain click
           still lands on the card underneath. */
        if (track.setPointerCapture) track.setPointerCapture(pointerId);
      }

      e.preventDefault();
      track.scrollLeft = startScrollLeft - delta;
    }

    function endDrag(e) {
      if (pointerId === null || (e && e.pointerId !== pointerId)) return;

      if (track.releasePointerCapture && track.hasPointerCapture && track.hasPointerCapture(pointerId)) {
        track.releasePointerCapture(pointerId);
      }

      pointerId = null;
      track.style.scrollSnapType = snapBack;
      track.classList.remove('is-dragging');
    }

    function onClick(e) {
      /* Swallow the click that terminates a drag so releasing over a card does
         not activate it. */
      if (!moved) return;
      moved = false;
      e.preventDefault();
      e.stopPropagation();
    }

    function onDragStart(e) {
      /* Native image/link dragging otherwise steals the gesture mid-swipe. */
      e.preventDefault();
    }

    track.addEventListener('pointerdown', onPointerDown);
    track.addEventListener('pointermove', onPointerMove);
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('click', onClick, true);
    track.addEventListener('dragstart', onDragStart);

    track.odditDragTeardown = function () {
      track.removeEventListener('pointerdown', onPointerDown);
      track.removeEventListener('pointermove', onPointerMove);
      track.removeEventListener('pointerup', endDrag);
      track.removeEventListener('pointercancel', endDrag);
      track.removeEventListener('click', onClick, true);
      track.removeEventListener('dragstart', onDragStart);
      delete track.dataset.dragInit;
    };
  }

  /* Centre the run of whole cards in the band.

     The leading gutter used to line the first card up with the 1340px content
     column, but the track bleeds to the right viewport edge — so that inset only
     ever applied on one side, leaving a wide empty margin on the left with cards
     running off the right. It is measured instead: fit as many whole cards as
     the band holds, then split what is left between the two edges, so the space
     before the first card matches the space after the last visible one. */
  function centreTrack(section, track) {
    var cards = track.children;
    if (!cards.length) return;

    var cardWidth = cards[0].offsetWidth;
    if (cardWidth <= 0) return;

    var step = cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : cardWidth;
    if (step <= 0) step = cardWidth;

    var gap = step - cardWidth;
    var available = track.clientWidth;
    /* +gap because a run of N cards has N-1 gaps, not N. */
    var fit = Math.max(1, Math.floor((available + gap) / step));

    /* Never reserve room for cards that do not exist. On a wide screen the band
       holds six 380px cards but the section only has five, and reserving the
       sixth put a gutter either side of a SIX-card run — so the five real cards
       sat left of centre with all the slack collecting on the right. Centring
       has to be on what is actually there. */
    if (fit > cards.length) fit = cards.length;

    /* Only centre once two cards fit. Below that the card is nearly as wide as
       the viewport, and centring it would trade the peek that tells a phone the
       row scrolls for a couple of pixels of even margin — on a 390px screen the
       380px card leaves 5px a side. Clearing the property hands those
       breakpoints back to the CSS gutter they were designed with. */
    if (fit < 2) {
      section.style.removeProperty('--oddit-benefits-carousel-track-gutter');
      return;
    }

    var gutter = Math.max(0, (available - (fit * step - gap)) / 2);
    section.style.setProperty(
      '--oddit-benefits-carousel-track-gutter',
      Math.round(gutter) + 'px'
    );
  }

  /* Prev/next arrows. Revealed only when the track actually overflows, so a
     section whose cards all fit shows no controls. */
  function initArrows(section, track) {
    var carousel = track.closest('.oddit-section-benefits-carousel__carousel');
    if (!carousel) return;

    var prev = carousel.querySelector('.oddit-section-benefits-carousel__arrow--prev');
    var next = carousel.querySelector('.oddit-section-benefits-carousel__arrow--next');
    if (!prev || !next) return;

    function page(direction) {
      var cards = track.children;
      var step = cards.length > 1
        ? cards[1].offsetLeft - cards[0].offsetLeft
        : (cards.length ? cards[0].offsetWidth : track.clientWidth);
      if (step <= 0) step = track.clientWidth;

      var styles = window.getComputedStyle(track);
      var inner =
        track.clientWidth -
        (parseFloat(styles.paddingLeft) || 0) -
        (parseFloat(styles.paddingRight) || 0);

      /* Floor, not round: paging by a card that is only peeking would leave the
         row misaligned against its snap points. */
      var fit = Math.max(1, Math.floor(inner / step));
      track.scrollBy({ left: direction * fit * step, behavior: 'smooth' });
    }

    function update() {
      var overflows = track.scrollWidth - track.clientWidth > 1;
      prev.hidden = !overflows;
      next.hidden = !overflows;
    }

    if (carousel.dataset.arrowsBound !== 'true') {
      carousel.dataset.arrowsBound = 'true';
      prev.addEventListener('click', function () { page(-1); });
      next.addEventListener('click', function () { page(1); });
    }

    track.odditUpdateArrows = update;
    update();
  }

  function measure(section) {
    var tracks = section.querySelectorAll(TRACK_SELECTOR);
    for (var i = 0; i < tracks.length; i++) {
      centreTrack(section, tracks[i]);
      if (typeof tracks[i].odditUpdateArrows === 'function') tracks[i].odditUpdateArrows();
    }
  }

  function initSection(section) {
    var tracks = section.querySelectorAll(TRACK_SELECTOR);
    for (var i = 0; i < tracks.length; i++) {
      initTrack(tracks[i]);
      centreTrack(section, tracks[i]);
      initArrows(section, tracks[i]);
    }

    /* A first measurement taken before the cards are laid out reads a zero card
       width and bails, which would leave the fallback gutter and hidden arrows
       for good — nothing else re-measures until a resize. */
    if (document.readyState !== 'complete') {
      window.addEventListener('load', function () {
        requestAnimationFrame(function () { measure(section); });
      }, { once: true });
    }

    var raf = null;
    window.addEventListener('resize', function () {
      if (raf) return;
      raf = requestAnimationFrame(function () {
        raf = null;
        measure(section);
      });
    }, { passive: true });
  }

  function destroySection(section) {
    var tracks = section.querySelectorAll(TRACK_SELECTOR);
    for (var i = 0; i < tracks.length; i++) {
      if (tracks[i].odditDragTeardown) tracks[i].odditDragTeardown();
    }
  }

  var sections = document.querySelectorAll(SECTION_SELECTOR);
  for (var i = 0; i < sections.length; i++) {
    initSection(sections[i]);
  }

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) {
      initSection(section);
    }
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) {
      destroySection(section);
    }
  });
})();
